import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, ArrowRight, RotateCcw, Share2 } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthenticationQuizPage = () => {
  const { slug } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tools/quizzes/${slug}`);
        if (!cancelled) setQuiz(res.data);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [slug]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await axios.post(`${API}/tools/quizzes/${slug}/score`, { answers });
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError('We could not score your quiz right now. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
    setCurrentIdx(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Quiz Not Found</h1>
        <Link to="/tools" className="btn-editorial">All Tools</Link>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  const totalQuestions = quiz.questions.length;
  const currentQuestion = quiz.questions[currentIdx];
  const allAnswered = quiz.questions.every((q) => answers[q.id] !== undefined);
  const progress = Math.round(((Object.keys(answers).length) / totalQuestions) * 100);

  return (
    <div data-testid={`quiz-page-${slug}`} className="bg-[#FAF9F6] min-h-screen">
      <SEO
        title={`${quiz.title} · Free Authentication Tool · Jewelry Refined`}
        description={quiz.intro.slice(0, 158)}
        url={`/tools/authenticate/${slug}`}
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{ items: [
          { name: 'Home', url: '/' },
          { name: 'Tools', url: '/tools' },
          { name: quiz.title.slice(0, 40), url: `/tools/authenticate/${slug}` },
        ]}}
      />

      {/* Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-12 lg:py-16 max-w-4xl">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-10">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/tools" className="hover:text-[#111] transition-colors">Tools</Link>
            <span>/</span>
            <span className="text-[#111]">Authentication</span>
          </nav>
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-5 h-5 text-[#C69C6D]" strokeWidth={1.5} />
            <p className="overline overline-gold">Authentication Tool</p>
          </div>
          <h1 className="font-heading font-light text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#111] mb-6">
            {quiz.title}
          </h1>
          <p className="text-lg text-[#555] leading-relaxed max-w-2xl">{quiz.intro}</p>
        </div>
      </section>

      <section className="section-editorial">
        <div className="container-editorial max-w-3xl">
          {result ? (
            <div data-testid="quiz-result" className="bg-white border border-[#E5E0D8] p-8 lg:p-12">
              <div className="flex items-start gap-6 mb-8 pb-8 border-b border-[#E5E0D8]">
                <div
                  className="flex-shrink-0 w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center font-heading text-3xl lg:text-4xl font-light"
                  style={{ background: result.tier.color, color: 'white' }}
                >
                  {result.score >= 0 ? `+${result.score}` : result.score}
                </div>
                <div>
                  <p className="overline overline-gold mb-2">
                    {result.score >= 0
                      ? `Score · ${result.score}/${result.max_possible}`
                      : `Score · ${result.score} (range −${result.max_possible} to +${result.max_possible})`}
                  </p>
                  <h2 className="font-heading text-3xl lg:text-4xl text-[#111] font-light leading-tight" style={{ color: result.tier.color }}>
                    {result.tier.label}
                  </h2>
                </div>
              </div>

              <p className="text-lg text-[#111] leading-relaxed mb-8">{result.tier.verdict}</p>

              <div className="bg-[#F3EFE6] p-6 lg:p-8 mb-10 border-l-4" style={{ borderColor: result.tier.color }}>
                <p className="overline mb-3">Next Step</p>
                <p className="text-[#111] leading-relaxed">{result.tier.next_step}</p>
              </div>

              {result.breakdown && result.breakdown.length > 0 && (
                <div className="mb-10">
                  <p className="overline overline-gold mb-6">Your Answers</p>
                  <ul className="space-y-3">
                    {result.breakdown.map((b) => (
                      <li key={b.id} className="flex items-start justify-between gap-6 pb-3 border-b border-[#E5E0D8]/60">
                        <div>
                          <p className="text-sm text-[#888] mb-1">{b.question}</p>
                          <p className="text-[#111]">{b.answer}</p>
                        </div>
                        <span className={`flex-shrink-0 font-heading text-lg ${b.score >= 0 ? 'text-[#2F6B3F]' : 'text-[#9B2C2C]'}`}>
                          {b.score >= 0 ? '+' : ''}{b.score}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                <button
                  data-testid="quiz-restart"
                  onClick={reset}
                  className="btn-editorial inline-flex items-center gap-3"
                >
                  <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                  Start Over
                </button>
                <Link to="/tools/roi-calculator" className="btn-editorial inline-flex items-center gap-3">
                  Try the ROI Calculator <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E5E0D8] p-8 lg:p-12">
              {/* Progress */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-3 text-xs tracking-[0.15em] uppercase text-[#888]">
                  <span>Question {currentIdx + 1} of {totalQuestions}</span>
                  <span>{progress}% complete</span>
                </div>
                <div className="h-px bg-[#E5E0D8] relative">
                  <div
                    className="absolute h-px bg-[#C69C6D] transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Question */}
              <div data-testid={`question-${currentQuestion.id}`}>
                <p className="overline overline-gold mb-4">Question {currentIdx + 1}</p>
                <h2 className="font-heading text-2xl lg:text-3xl text-[#111] font-light leading-tight mb-4">
                  {currentQuestion.question}
                </h2>
                {currentQuestion.help && (
                  <p className="text-sm italic text-[#555] mb-8 leading-relaxed border-l-2 border-[#C69C6D] pl-4">
                    {currentQuestion.help}
                  </p>
                )}

                <div className="space-y-3 mb-10">
                  {currentQuestion.options.map((opt, i) => {
                    const isSelected = answers[currentQuestion.id] === i;
                    return (
                      <button
                        key={i}
                        data-testid={`option-${currentQuestion.id}-${i}`}
                        onClick={() => setAnswers({ ...answers, [currentQuestion.id]: i })}
                        className={`w-full text-left px-5 py-4 border transition-all ${
                          isSelected
                            ? 'border-[#111] bg-[#111] text-white'
                            : 'border-[#E5E0D8] text-[#111] hover:border-[#111] bg-white'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-[#E5E0D8]">
                  <button
                    data-testid="quiz-back"
                    onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                    disabled={currentIdx === 0}
                    className="text-xs tracking-[0.15em] uppercase text-[#888] hover:text-[#111] disabled:opacity-0 disabled:cursor-default transition-colors"
                  >
                    ← Back
                  </button>
                  {currentIdx === totalQuestions - 1 ? (
                    <button
                      data-testid="quiz-submit"
                      onClick={submit}
                      disabled={!allAnswered || submitting}
                      className="btn-editorial inline-flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Calculating…' : 'See My Verdict'}
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  ) : (
                    <button
                      data-testid="quiz-next"
                      onClick={() => setCurrentIdx(currentIdx + 1)}
                      disabled={answers[currentQuestion.id] === undefined}
                      className="btn-editorial inline-flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next Question
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                {submitError && (
                  <p data-testid="quiz-submit-error" className="mt-6 text-sm text-[#9B2C2C]">
                    {submitError}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Footer disclaimer */}
          <p className="text-xs text-[#888] mt-10 max-w-2xl leading-relaxed">
            This tool is editorial guidance based on common authentication checkpoints — not a substitute for in-person professional authentication. For purchases over $5,000 we recommend a paid third-party report from Real Authentication or Entrupy before completing payment.
            <Share2 className="w-3 h-3 inline-block ml-1 text-[#888]" strokeWidth={1.5} />
          </p>
        </div>
      </section>
    </div>
  );
};

export default AuthenticationQuizPage;
